import React from 'react';
import ReactECharts from 'echarts-for-react';

import { BarLineComboChartProps } from 'types/e-charts';

const BarLineComboChart: React.FC<BarLineComboChartProps> = ({
  categories,
  series,
  barColors,
  height = 236,
  width = '100%',
  showXAxisLabels = true,
  leftYAxisLabel = 'Count',
  rightYAxisLabel = 'Value',
  showTooltip = true,
  showLegend = false,
  showGrid = true,
  barWidth = 40,
  className = '',
  customOptions = {},
  leftYAxisFormatter,
  rightYAxisFormatter,
  tooltipValueFormatter
}) => {
  // Split bar and line series
  const barSeries = series.filter((s) => s.type === 'bar');
  const lineSeries = series.filter((s) => s.type === 'line');

  // For unique bar colors, use itemStyle on each bar series
  const echartSeries = [
    ...barSeries.map((s, idx) => ({
      name: s.name,
      type: 'bar',
      data: s.data,
      yAxisIndex: 0,
      itemStyle: {
        color: s.color || (barColors ? barColors[idx] : undefined)
      },
      barWidth,
      stack: 'bar'
    })),
    ...lineSeries.map((s) => ({
      name: s.name,
      type: 'line',
      data: s.data,
      yAxisIndex: 1,
      smooth: true,
      lineStyle: { width: 2, color: s.color },
      itemStyle: { color: s.color },
      symbol: 'circle',
      symbolSize: 8
    }))
  ];

  const defaultOption = {
    tooltip: showTooltip
      ? {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          textStyle: { fontSize: 12 },
          formatter: tooltipValueFormatter
            ? (params: any) => {
                if (!Array.isArray(params)) return '';
                return params
                  .map((param: any) => {
                    const formattedValue = tooltipValueFormatter(
                      param.value,
                      param.seriesName
                    );
                    return `${param.marker} ${param.seriesName}: ${formattedValue}`;
                  })
                  .join('<br/>');
              }
            : undefined
        }
      : { show: false },
    legend: { show: showLegend },
    grid: { left: 60, right: 60, top: 10, bottom: 30, containLabel: true },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        show: showXAxisLabels,
        fontWeight: 400,
        fontSize: 12,
        color: '#242424'
      },
      axisTick: { alignWithLabel: true }
    },
    yAxis: [
      {
        type: 'value',
        name: leftYAxisLabel,
        position: 'left',
        splitLine: {
          show: showGrid,
          lineStyle: { color: '#E7EAEE', width: 0.5 }
        },
        axisLabel: {
          fontWeight: 400,
          fontSize: 12,
          color: '#333333',
          formatter: leftYAxisFormatter
        },
        nameTextStyle: {
          fontWeight: 400,
          fontSize: 12,
          color: '#7E7E7E'
        }
      },
      {
        type: 'value',
        name: rightYAxisLabel,
        position: 'right',
        splitLine: { show: false },
        axisLabel: {
          fontWeight: 400,
          fontSize: 12,
          color: '#333333',
          formatter: rightYAxisFormatter
        },
        nameTextStyle: {
          fontWeight: 400,
          fontSize: 12,
          color: '#7E7E7E'
        }
      }
    ],
    series: echartSeries
  };

  // Merge default options with custom options
  const option = { ...defaultOption, ...customOptions };

  return (
    <div className={`w-full ${className}`}>
      <ReactECharts option={option} style={{ width, height }} />
    </div>
  );
};

export default BarLineComboChart;
